import { PlugZapIcon, UsersRoundIcon, XIcon } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useState } from "react";

import type { UpdateTravelAgencyCompanyPayload } from "@upclaim/sdk/src/data-contracts";

import { Combobox } from "@/components/combobox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAdmins } from "@/features/admins/api/use-admins";
import { TeamSelect } from "@/features/admins/components/team-select";
import { cn } from "@/lib/utils";

import type { BusinessTravelCompany } from "../api/use-companies";
import { useUpdateCompany } from "../api/use-companies";
import { getCompanyTenantId } from "../lib/companies-rows";
import { type AmadeusLinkType, amadeusLinkLabels, getAmadeusLinkField } from "../lib/create-company-schema";

const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CompanyConfigurationTabProps {
  company: BusinessTravelCompany;
  amadeusLinkType: AmadeusLinkType | null;
}

/**
 * Everything that decides which reservations reach this company, then who handles them.
 * The two sections follow that order because it is the order in which a PNR travels:
 * it is first matched to the company, then routed to a person.
 */
export function CompanyConfigurationTab({ company, amadeusLinkType }: CompanyConfigurationTabProps) {
  const { data: admins = [] } = useAdmins();
  const updateCompany = useUpdateCompany();

  const [input, setInput] = useState<UpdateTravelAgencyCompanyPayload>({});

  function patch(partial: UpdateTravelAgencyCompanyPayload) {
    setInput((current) => ({ ...current, ...partial }));
  }

  function value<K extends keyof UpdateTravelAgencyCompanyPayload>(
    key: K,
    fallback: UpdateTravelAgencyCompanyPayload[K],
  ): UpdateTravelAgencyCompanyPayload[K] {
    return input[key] !== undefined ? input[key] : fallback;
  }

  const accountManagers = admins
    .filter((admin) => admin.adminRole.name === "CUSTOMER_ACCOUNT_MANAGER")
    .map((admin) => ({ id: admin.id, fullName: admin.fullName }));

  const amadeusLinkField = amadeusLinkType ? getAmadeusLinkField(amadeusLinkType) : "rawGdsName";
  const domain = value("domain", company.domain ?? undefined) ?? "";
  const mainContactEmail = value("mainContactEmail", company.mainContactEmail) ?? "";
  const signaturesCc =
    value("passengersSignaturesForwardEmail", company.passengersSignaturesForwardEmail) ?? "";
  const additionalDomains = value("additionalDomains", company.additionalDomains) ?? [];

  // Validated as you type, so nothing is discovered at save time.
  const errors = {
    domain: domain && !DOMAIN_REGEX.test(domain) ? "Nom de domaine invalide (ex: acme.fr)" : undefined,
    mainContactEmail:
      mainContactEmail && !EMAIL_REGEX.test(mainContactEmail) ? "Adresse email invalide" : undefined,
    signaturesCc: signaturesCc && !EMAIL_REGEX.test(signaturesCc) ? "Adresse email invalide" : undefined,
  };

  const isDirty = Object.keys(input).length > 0;
  const hasError = Object.values(errors).some(Boolean);

  async function submit() {
    try {
      await updateCompany.mutateAsync({ companyId: company.id, data: input });
      setInput({});
    } catch {
      // Error toast handled by notify.promise.
    }
  }

  return (
    <div className="space-y-8 pb-20">
      <Section
        icon={<PlugZapIcon className="size-4" aria-hidden="true" />}
        title="Rattachement des réservations"
        description="Ces valeurs sont comparées aux PNR pour retrouver l'entreprise. Sans elles, ses dossiers ne lui sont jamais attribués."
      >
        <Field
          label="Nom de domaine"
          hint="Domaine email des voyageurs."
          error={errors.domain}
          htmlFor="config_domain"
        >
          <Input
            id="config_domain"
            value={domain}
            placeholder="acme.fr"
            aria-invalid={Boolean(errors.domain)}
            onChange={(event) => patch({ domain: event.currentTarget.value })}
          />
        </Field>

        <Field
          label="Identifiant OSI YYOIN"
          hint="Renseigné dans la remarque OSI du PNR."
          htmlFor="config_bluebiz"
        >
          <Input
            id="config_bluebiz"
            value={value("bluebizIdentifier", company.bluebizIdentifier) ?? ""}
            placeholder="FRACME"
            onChange={(event) => patch({ bluebizIdentifier: event.currentTarget.value || null })}
          />
        </Field>

        <Field
          label="Domaines additionnels"
          hint="Filiales ou anciens domaines encore utilisés par les voyageurs."
          htmlFor="config_additional_domains"
        >
          <DomainList
            domains={additionalDomains}
            onChange={(nextDomains) => patch({ additionalDomains: nextDomains })}
          />
        </Field>

        {amadeusLinkType ? (
          <Field
            label={amadeusLinkLabels[amadeusLinkType].label}
            hint={amadeusLinkLabels[amadeusLinkType].help}
            htmlFor="config_amadeus_link"
          >
            <Input
              id="config_amadeus_link"
              value={value(amadeusLinkField, company[amadeusLinkField]) ?? ""}
              placeholder={amadeusLinkLabels[amadeusLinkType].placeholder}
              onChange={(event) => patch({ [amadeusLinkField]: event.currentTarget.value || null })}
            />
          </Field>
        ) : null}
      </Section>

      <Section
        icon={<UsersRoundIcon className="size-4" aria-hidden="true" />}
        title="Traitement des dossiers"
        description="Qui suit cette entreprise, et où partent les emails la concernant."
      >
        <Field label="Chargé de compte" hint="Notifié des dossiers de cette entreprise.">
          <Combobox
            className="w-full"
            items={accountManagers}
            value={
              accountManagers.find(
                (agent) => agent.id === value("accountManagerId", company.accountManagerId),
              ) ?? null
            }
            onChange={(agent) => patch({ accountManagerId: agent?.id ?? null })}
            getKey={(agent) => agent.id}
            getLabel={(agent) => agent.fullName}
            placeholder="Aucun"
            unselectLabel="Aucun"
            searchPlaceholder="Rechercher un chargé de compte..."
            emptyMessage="Aucun chargé de compte trouvé"
          />
        </Field>

        {/* Renders nothing when the agency has no team — hence its own label and hint. */}
        <TeamSelect
          tenantId={getCompanyTenantId(company) ?? undefined}
          value={value("teamId", company.teamId) ?? null}
          onChange={(teamId) => patch({ teamId })}
          hint="Équipe de l'agence à qui les dossiers sont assignés."
        />

        <Field
          label="Email dédié entreprise"
          hint="Adresse interne de l'agence où router les notifications."
          error={errors.mainContactEmail}
          htmlFor="config_main_email"
        >
          <Input
            id="config_main_email"
            value={mainContactEmail}
            aria-invalid={Boolean(errors.mainContactEmail)}
            onChange={(event) => patch({ mainContactEmail: event.currentTarget.value || null })}
          />
        </Field>

        <Field
          label="Signatures CC"
          hint="Copie des demandes de signature envoyées aux voyageurs."
          error={errors.signaturesCc}
          htmlFor="config_signatures_cc"
        >
          <Input
            id="config_signatures_cc"
            value={signaturesCc}
            aria-invalid={Boolean(errors.signaturesCc)}
            onChange={(event) =>
              patch({ passengersSignaturesForwardEmail: event.currentTarget.value || null })
            }
          />
        </Field>

        <div className="flex items-start gap-3 rounded-lg border p-3">
          <Switch
            id="config_direct_contact"
            checked={
              value("isDirectPassengerContactAllowed", company.isDirectPassengerContactAllowed) ?? false
            }
            onCheckedChange={(isDirectPassengerContactAllowed) => patch({ isDirectPassengerContactAllowed })}
          />
          <Label htmlFor="config_direct_contact" className="block text-sm font-normal text-gray-700">
            Contact des voyageurs en direct
            <span className="mt-0.5 block text-xs text-gray-500">
              Désactivé, les demandes de signature passent par l'entreprise.
            </span>
          </Label>
        </div>
      </Section>

      {/* Appears only once something changed — a translucent bar over the scrolling content. */}
      <div
        className={cn(
          "sticky bottom-0 -mx-1 flex items-center justify-end gap-3 rounded-lg border bg-white/80 px-3 py-2 backdrop-blur-md transition-opacity duration-200",
          "motion-reduce:transition-none",
          isDirty ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        aria-hidden={!isDirty}
      >
        <span className="mr-auto text-xs text-gray-500">Modifications non enregistrées</span>
        <Button type="button" variant="ghost" onClick={() => setInput({})}>
          Annuler
        </Button>
        <Button type="button" disabled={updateCompany.isPending || hasError} onClick={() => void submit()}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

interface SectionProps {
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
}

/** A titled group — proximity and a one-line why, so no field needs a manual. */
function Section({ icon, title, description, children }: SectionProps) {
  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h3 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-gray-900">
          <span className="text-gray-400">{icon}</span>
          {title}
        </h3>
        <p className="text-xs leading-relaxed text-gray-500">{description}</p>
      </header>
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  htmlFor?: string;
  children: ReactNode;
}

function Field({ label, hint, error, htmlFor, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="block text-sm font-medium text-gray-900">
        {label}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-red-500">{error}</p>
      ) : hint ? (
        <p className="text-xs leading-relaxed text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
}

interface DomainListProps {
  domains: string[];
  onChange: (domains: string[]) => void;
}

/**
 * Domains as removable chips rather than a comma-separated string: what is stored is a list,
 * so the control shows a list — no syntax to remember, and removing one is a single click.
 */
function DomainList({ domains, onChange }: DomainListProps) {
  const [draft, setDraft] = useState("");

  function addDraft() {
    const domain = draft.trim().toLowerCase();
    if (!domain || domains.includes(domain)) {
      setDraft("");
      return;
    }
    onChange([...domains, domain]);
    setDraft("");
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addDraft();
    }
    if (event.key === "Backspace" && !draft && domains.length) {
      onChange(domains.slice(0, -1));
    }
  }

  return (
    <div className="space-y-2">
      <Input
        id="config_additional_domains"
        value={draft}
        placeholder="acme-voyages.com puis Entrée"
        onChange={(event) => setDraft(event.currentTarget.value)}
        onKeyDown={onKeyDown}
        onBlur={addDraft}
      />
      {domains.length ? (
        <ul className="flex flex-wrap gap-1.5">
          {domains.map((domain) => (
            <li key={domain}>
              <Badge variant="secondary" className="gap-1 pr-1">
                {domain}
                <button
                  type="button"
                  className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-black/5 hover:text-foreground"
                  onClick={() => onChange(domains.filter((item) => item !== domain))}
                  aria-label={`Retirer ${domain}`}
                >
                  <XIcon className="size-3" aria-hidden="true" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
